/** Underline-style sub-tab bar shared by pages that split content into
 * views (Market sections, Strategies horizons). Generic over the tab id
 * so each page keeps its own literal union type. */

import type { ReactElement } from "react";
import { cn } from "@/lib/cn";

export interface TabDef<T extends string> {
  id: T;
  label: string;
  icon: string;
  /** Optional item count shown after the label. */
  count?: number;
}

export function TabBar<T extends string>({
  tabs,
  value,
  onChange,
}: Readonly<{
  tabs: readonly TabDef<T>[];
  value: T;
  onChange: (tab: T) => void;
}>): ReactElement {
  return (
    <div className="mb-4 flex gap-1 border-b border-line">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          className={cn(
            "-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors",
            t.id === value
              ? "border-accent font-medium text-ink"
              : "border-transparent text-ink-secondary hover:text-ink",
          )}
          onClick={() => {
            onChange(t.id);
          }}
        >
          <i className={cn("ti", t.icon)} aria-hidden="true" />
          {t.label}
          {t.count !== undefined && (
            <span className="text-xs text-ink-muted">{String(t.count)}</span>
          )}
        </button>
      ))}
    </div>
  );
}
